/**
 * src/services/zenexTradeService.ts
 *
 * Browser wrapper around GeoTrust's `/api/v2/zenex/*` endpoints. The server
 * layer (`server/v2/routes/zenex.js`) is what talks to the Zenex SDK, Pyth
 * price service and the Stellar RPC. This module is a thin typed fetch
 * client — it does NOT import the SDK or stellar-sdk, so it stays safe to
 * call from any React component without blowing up the bundle.
 */

const BASE = '/api/v2/zenex';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ZenexMarketInfo {
  symbol: string;
  feed_id: number;
  price_decimals: number;
}

/**
 * Offline fallback market map — a 1-for-1 mirror of GeoLink's
 * `ZENEX_MARKET_FEED_MAP` and the server's copy in
 * `server/v2/lib/zenex.js`. The panel seeds its market state with this
 * constant so rule labels never flash "market 0" / "market ?" while
 * `/api/v2/zenex/markets` is loading.
 *
 * If any of these values drift, update GeoLink + server + client together.
 */
// String keys so this merges cleanly with the server's /markets response
// (Node's `res.json({ markets: {1: ...} })` serialises object keys as strings).
export const ZENEX_MARKET_FEED_MAP: Readonly<Record<string, ZenexMarketInfo>> = Object.freeze({
  '1': Object.freeze({ symbol: 'XLM', feed_id: 23, price_decimals: 8 }),
  '2': Object.freeze({ symbol: 'BTC', feed_id: 1,  price_decimals: 8 }),
  '3': Object.freeze({ symbol: 'ETH', feed_id: 2,  price_decimals: 8 }),
});

/** Upstream Zenex price service base URL. Matches GeoLink's constant. */
export const ZENEX_TESTNET_PRICE_BASE_URL = 'http://api-testnet.zenex.trade/prices';

export interface ZenexMarketsResponse {
  success: boolean;
  contract: { trading: string; vault: string };
  markets: Record<string, ZenexMarketInfo>;
  error?: string;
}

export interface ZenexPriceBytes {
  success: boolean;
  feedId: number;
  priceHex: string;
  priceBase64: string;
  meta: {
    price?: string;
    exponent?: number;
    confidence?: string;
    timestamp?: number;
  } | null;
  fetchedAt: string;
  upstream: { status: number; contentType: string; url: string };
  error?: string;
}

export interface PrepareOpenMarketRequest {
  user: string; // G- or C-address
  market_id: number | string;
  collateral: string;    // i128 decimal string
  notional_size: string; // i128 decimal string
  is_long: boolean;
  take_profit?: string;
  stop_loss?: string;
  /** Optional — server fetches if omitted. */
  priceHex?: string;
}

export interface PrepareCloseRequest {
  user: string;
  position_id: number;
  /** If omitted, server fetches using `market_id`. */
  priceHex?: string;
  /** Required when `priceHex` is omitted. */
  market_id?: number | string;
}

export interface PrepareTxResponse {
  success: boolean;
  unsignedXdr: string;
  latestLedger: number;
  authMode: 'smart-account' | 'classic';
  priceHex: string;
  feedId?: number;
  market: { id: number; symbol: string; feed_id: number; price_decimals: number } | null;
  pythMeta?: ZenexPriceBytes['meta'];
  error?: string;
}

export interface ZenexOpenPosition {
  id: number;
  market_id: number;
  filled: boolean;
  notional_size: string;
  /** Extended fields only populated by the plural lookup. */
  is_long?: boolean | null;
  entry_price?: string | null;
  collateral?: string | null;
}

export interface LookupPositionResponse {
  success: boolean;
  position: ZenexOpenPosition | null;
  error?: string;
}

export interface LookupPositionsResponse {
  success: boolean;
  positions: ZenexOpenPosition[];
  error?: string;
}

/**
 * Structured error payload emitted by the /api/v2/zenex/* routes.
 *
 * The server runs the SDK's `parseError` on the raw simulation message to
 * recover the numeric contract code (e.g. 720 = PositionNotFound), then
 * returns that alongside a human-readable `error`, a short `code`
 * machine-tag, and an optional actionable `hint`. The UI keys on `code`
 * for reactive behaviour (e.g. invalidating the cached open-position
 * when the contract says it no longer exists) and renders `hint` as the
 * primary help text.
 */
export type ZenexErrorCode =
  // Runtime-surfaced tags our UI cares about.
  | 'missing_trustline'
  | 'insufficient_collateral'
  | 'position_not_found'
  | 'position_not_pending'
  | 'position_too_new'
  | 'price_feed_stale'
  | 'price_feed_invalid'
  | 'market_not_registered'
  | 'market_disabled'
  | 'notional_below_min'
  | 'notional_above_max'
  | 'leverage_too_high'
  | 'utilization_cap'
  | 'contract_on_ice'
  | 'contract_frozen'
  | 'unauthorized'
  // Anything we haven't special-cased falls back to the SDK enum name
  // lower-cased (e.g. 'tradingmathoverflow') — still useful as a grep key.
  | string;

export interface ZenexStructuredError extends Error {
  status?:            number;
  code?:              ZenexErrorCode;
  hint?:              string;
  asset?:             { code: string; issuer: string };
  contractErrorCode?: number;
  contractErrorName?: string;
  rawError?:          string;
}

// ── Internals ─────────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = `${BASE}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  const resp = await fetch(url, { ...init, headers });
  const ct = resp.headers.get('content-type') || '';
  const parsed = ct.includes('application/json')
    ? await resp.json().catch(() => ({}))
    : await resp.text();
  if (!resp.ok) {
    const body = typeof parsed === 'object' && parsed ? parsed as {
      error?:             string;
      hint?:              string;
      code?:              ZenexErrorCode;
      asset?:             { code: string; issuer: string };
      contractErrorCode?: number;
      contractErrorName?: string;
      rawError?:          string;
    } : null;
    const msg = body?.error || `HTTP ${resp.status}: ${resp.statusText}`;
    const err = new Error(msg) as ZenexStructuredError;
    err.status            = resp.status;
    err.code              = body?.code;
    err.hint              = body?.hint;
    err.asset             = body?.asset;
    err.contractErrorCode = body?.contractErrorCode;
    err.contractErrorName = body?.contractErrorName;
    err.rawError          = body?.rawError;
    throw err;
  }
  return parsed as T;
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function getZenexMarkets(): Promise<ZenexMarketsResponse> {
  return request<ZenexMarketsResponse>('/markets');
}

/** Fetch a Pyth price update for a feed id. */
export async function fetchZenexPrice(feedId: number | string): Promise<ZenexPriceBytes> {
  const id = encodeURIComponent(String(feedId));
  return request<ZenexPriceBytes>(`/prices/${id}`);
}

/**
 * Build an unsigned XDR for `TradingContract.open_market`. When the user is
 * a C-address, the response carries a passkey-signable XDR with the
 * `__check_auth` footprint already expanded; a classic G-address gets an
 * already-assembled, simulated XDR that it can sign with any wallet.
 */
export async function prepareOpenMarket(
  body: PrepareOpenMarketRequest,
): Promise<PrepareTxResponse> {
  return request<PrepareTxResponse>(`/prepare-open-market`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Build an unsigned XDR for `TradingContract.close_position`. */
export async function prepareClosePosition(
  body: PrepareCloseRequest,
): Promise<PrepareTxResponse> {
  return request<PrepareTxResponse>(`/prepare-close-position`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Find the user's most-recent filled, non-zero-notional position. Returns
 * `null` if nothing matches — callers should then `/cancel` the pending
 * close action with reason `other` per the integration guide.
 */
export async function lookupOpenPosition(body: {
  user: string;
  market_id?: number | string;
  maxScan?: number;
}): Promise<LookupPositionResponse> {
  return request<LookupPositionResponse>(`/lookup-open-position`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * List every filled, non-zero-notional position the user owns — used by
 * the pending-trades panel to fan one GeoLink close_position action out
 * into one actionable card per open position. `market_id` narrows the
 * scan; omit it to get positions across all markets.
 */
export async function lookupOpenPositions(body: {
  user: string;
  market_id?: number | string;
  maxScan?: number;
}): Promise<LookupPositionsResponse> {
  return request<LookupPositionsResponse>(`/lookup-open-position`, {
    method: 'POST',
    body: JSON.stringify({ ...body, list: true }),
  });
}

// ── Submission helper (reuses /api/passkey-kit/fee-pay-and-submit) ───────────
// The server layer already has a battle-tested submit path for
// smart-account transactions; we just forward the signed XDR to it. For
// G-address (classic) flows the caller should submit via Horizon or
// Freighter.submitTransaction directly — this helper is smart-account only.

export interface FeePayAndSubmitResponse {
  success: boolean;
  hash?: string;
  ledger?: number;
  error?: string;
}

export async function feePayAndSubmit(xdrWithPasskeySig: string): Promise<FeePayAndSubmitResponse> {
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const url = `${backendUrl}/api/passkey-kit/fee-pay-and-submit`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ xdrWithPasskeySig }),
  });
  const parsed = await resp.json().catch(() => ({}));
  if (!resp.ok || !(parsed as { success?: boolean }).success) {
    const msg = (parsed as { error?: string })?.error || `HTTP ${resp.status}: ${resp.statusText}`;
    throw new Error(msg);
  }
  return parsed as FeePayAndSubmitResponse;
}

// ── Classic submit (G-address / Freighter / WalletConnect) ───────────────────
// Zenex's `open_market` / `close_position` are Soroban contract invocations.
// When a G-address signs the classically-assembled XDR, we still need to
// submit it through the Soroban RPC (NOT Horizon). This mirrors the pattern
// in `src/App.tsx` (see `rpc.sendTransaction` / `rpc.getTransaction`).

/**
 * Submit a classically-signed Soroban XDR via the Soroban RPC's
 * `sendTransaction` endpoint, then poll `getTransaction(hash)` until it
 * reports SUCCESS / FAILED or the timeout expires. On success returns the
 * hash + final ledger; on timeout returns the hash so callers can still
 * reconcile it against `/api/contracts/rules/pending/trades/:id/complete`.
 */
export async function submitClassicSorobanXdr(
  signedXdr: string,
  opts?: {
    networkPassphrase?: string;
    rpcUrl?: string;
    pollIntervalMs?: number;
    timeoutMs?: number;
  },
): Promise<{ hash: string; ledger?: number }> {
  // Lazy import — stellar-sdk is already in the bundle but keeping it lazy
  // here avoids pulling it when the panel only uses the passkey path.
  const { TransactionBuilder, rpc } = await import('@stellar/stellar-sdk');

  const networkPassphrase =
    opts?.networkPassphrase
    ?? process.env.REACT_APP_NETWORK_PASSPHRASE
    ?? 'Test SDF Network ; September 2015';
  const rpcUrl =
    opts?.rpcUrl
    ?? process.env.REACT_APP_SOROBAN_RPC_URL
    ?? 'https://soroban-testnet.stellar.org';

  const server = new rpc.Server(rpcUrl, { allowHttp: rpcUrl.startsWith('http://') });
  const tx = TransactionBuilder.fromXDR(signedXdr, networkPassphrase);

  const sendResponse = await server.sendTransaction(tx);
  if (sendResponse.status === 'ERROR') {
    const details = JSON.stringify(
      (sendResponse as { errorResult?: unknown }).errorResult ?? sendResponse,
    );
    throw new Error(`Soroban sendTransaction rejected: ${details}`);
  }
  const hash = sendResponse.hash;
  if (!hash) throw new Error('Soroban sendTransaction did not return a hash');

  const deadline = Date.now() + (opts?.timeoutMs ?? 45_000);
  const interval = opts?.pollIntervalMs ?? 1500;
  while (Date.now() < deadline) {
    try {
      const info = await server.getTransaction(hash);
      if (info.status === 'SUCCESS') {
        const ledger = (info as { ledger?: number }).ledger;
        return { hash, ledger };
      }
      if (info.status === 'FAILED') {
        const resultXdr = (info as { resultXdr?: { toXDR?: (fmt: string) => string } }).resultXdr;
        const hint = resultXdr?.toXDR ? resultXdr.toXDR('base64') : '';
        throw new Error(`Transaction ${hash} failed on-chain${hint ? ` (${hint})` : ''}`);
      }
      // NOT_FOUND → still being ingested; fall through and poll again.
    } catch (e) {
      // getTransaction can 404 briefly right after submit — only re-throw if
      // it's our own "failed on-chain" error.
      if (String((e as Error)?.message || '').includes('failed on-chain')) throw e;
    }
    await new Promise(r => setTimeout(r, interval));
  }
  // Timed out waiting for ingestion, but the tx was accepted by the RPC.
  // Returning the hash lets the caller finish the GeoLink `/complete` flow
  // and resolve the ledger later.
  return { hash };
}
